import { useEffect, useState } from "react";
import { Wallet as WalletIcon, CreditCard, ExternalLink, AlertTriangle } from "lucide-react";
import { api } from "../lib/api.js";
import { useAuth } from "../auth.jsx";
import { PageHeader, Card, Spinner, timeAgo } from "../components/ui.jsx";

// Offered amounts are derived from the server's minimum, never hardcoded. A fixed £10 preset
// was always rejected (the floor is £25, or last month's usage if higher) and the rejection
// wiped the page — so the UI was advertising a button that could not work.
const presetsFor = (minPence) => {
  const base = Math.max(2500, Math.ceil((minPence || 0) / 500) * 500);
  return [base, base * 2, base * 4];
};
const gbp = (pence) => `£${(pence / 100).toFixed(2)}`;

export default function Wallet() {
  const { user } = useAuth();
  const isOwner = user?.orgRole === "owner";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [amount, setAmount] = useState(2500); // pence; re-seeded from min_topup_pence on load
  const [busy, setBusy] = useState(false);
  const [ar, setAr] = useState(null); // auto-recharge settings { enabled, thresholdPence, amountPence, consecutiveFails, needsAction }
  const [arBusy, setArBusy] = useState(false);

  const load = () =>
    api.wallet()
      .then((d) => { setData(d); setError(null); if (d?.min_topup_pence) setAmount((a) => Math.max(a, d.min_topup_pence)); })
      .catch((e) => setError((previous) => previous || e));
  // Wrap in a block so the effect returns undefined — passing `load` directly made
  // the effect's "cleanup" its returned Promise, which React calls on unmount → crash.
  useEffect(() => {
    // Back from Stripe Checkout: the webhook normally credits the wallet, but if it
    // didn't arrive the money sits at Stripe uncredited — reconcile before loading.
    if (new URLSearchParams(window.location.search).get("topup") === "success") {
      window.history.replaceState({}, "", window.location.pathname);
      api.reconcileTopups().catch(() => {}).then(load);
    } else load();
    api.autoRecharge().then(setAr).catch(() => setAr(null));
    const refresh = setInterval(load, 30_000);
    return () => clearInterval(refresh);
  }, []);

  const saveAr = async () => {
    setArBusy(true);
    try { setAr(await api.setAutoRecharge({ enabled: ar.enabled, thresholdPence: ar.thresholdPence, amountPence: ar.amountPence })); }
    catch (e) { setError(e); }
    finally { setArBusy(false); }
  };

  const topup = async (pence) => {
    setBusy(true);
    try { const { url } = await api.topup(pence); window.location.href = url; }
    catch (e) { setError(e); setBusy(false); }
  };
  const openPortal = async () => {
    setBusy(true);
    try { const { url } = await api.billingPortal(); window.location.href = url; }
    catch (e) { setError(e); setBusy(false); }
  };

  if (error) return <div className="page"><p style={{ color: "var(--err)" }}>Failed: {error.message}</p></div>;
  if (!data) return <div className="flex h-40 items-center justify-center" style={{ color: "var(--text-muted)" }}><Spinner className="mr-2" /> Loading…</div>;

  return (
    <div className="page">
      <PageHeader title="Wallet" subtitle="Your prepaid credit wallet." />

      {data.billing_status === "arrears" && (
        <Card className="mb-6" style={{ borderColor: "var(--err)" }}>
          <div className="flex items-start gap-2" style={{ color: "var(--err)" }}>
            <AlertTriangle size={16} style={{ marginTop: 2, flexShrink: 0 }} />
            <span className="text-sm">
              Your balance is {gbp(data.balance_pence)}. Nothing is suspended yet
              {data.overdraft_limit_pence
                ? `, but services are suspended once the balance reaches −${gbp(data.overdraft_limit_pence)}.`
                : "."}{" "}
              Top up to clear the arrears.
            </span>
          </div>
        </Card>
      )}

      {/* The saved card failed 3DS, so it can never be charged off-session again and auto
          top-up has stopped. Only one thing fixes it — a top-up the customer is present for
          — so say that, rather than just reporting a failure count. Also emailed. */}
      {ar?.needsAction === "consent_required" && (
        <Card className="mb-6" style={{ borderColor: "var(--warn)" }}>
          <div className="flex items-start gap-2" style={{ color: "var(--warn)" }}>
            <AlertTriangle size={16} style={{ marginTop: 2, flexShrink: 0 }} />
            <span className="text-sm">
              Automatic top-up has been paused. We now ask you to switch it on explicitly and confirm
              the amount, rather than leaving it on by default. Your settings are unchanged — tick the
              box below and save to turn it back on.
            </span>
          </div>
        </Card>
      )}

      {ar?.needsAction === "card_authentication_required" && (
        <Card className="mb-6" style={{ borderColor: "var(--err)" }}>
          <div className="flex items-start gap-2" style={{ color: "var(--err)" }}>
            <AlertTriangle size={16} style={{ marginTop: 2, flexShrink: 0 }} />
            <span className="text-sm">
              Your bank asked to verify the card we have on file, which can't be done while you're away.
              Automatic top-up is paused. Make one top-up below — you'll confirm it with your bank, and
              that card works automatically again afterwards.
            </span>
          </div>
        </Card>
      )}

      <Card className="mb-6">
        <div className="flex items-center gap-2 mb-1" style={{ color: "var(--text-muted)" }}>
          <WalletIcon size={16} /><span className="text-sm">Wallet balance</span>
        </div>
        <div style={{ fontSize: 32, fontWeight: 700, color: data.balance_pence < 0 ? "var(--err)" : "var(--text)" }}>
          {gbp(data.balance_pence)}
        </div>

        {isOwner && (
          <div className="mt-4">
            <div className="flex flex-wrap gap-2 items-center">
              {presetsFor(data.min_topup_pence).map((p) => (
                <button key={p} className={`btn ${amount === p ? "btn-primary" : ""}`} onClick={() => setAmount(p)}>{gbp(p)}</button>
              ))}
              <input className="input" type="number" min="1" step="1" style={{ width: 120 }}
                value={amount / 100} onChange={(e) => setAmount(Math.round(Number(e.target.value) * 100))} />
              <button
                className="btn btn-primary"
                disabled={busy || amount < (data.min_topup_pence || 2500)}
                title={amount < (data.min_topup_pence || 2500) ? `Minimum top-up is ${gbp(data.min_topup_pence || 2500)}` : undefined}
                onClick={() => topup(amount)}
              >
                <CreditCard size={14} /> Top up
              </button>
              <button className="btn" disabled={busy} onClick={openPortal}><ExternalLink size={14} /> Manage in Stripe</button>
            </div>
          </div>
        )}
      </Card>

      {isOwner && ar && (
        <Card className="mb-6">
          <div className="flex items-center gap-2 mb-3" style={{ color: "var(--text-muted)" }}>
            <CreditCard size={16} /><span className="text-sm">Auto-recharge</span>
          </div>
          <label className="flex items-center gap-2 text-sm mb-3" style={{ color: "var(--text)" }}>
            <input type="checkbox" checked={!!ar.enabled} onChange={(e) => setAr({ ...ar, enabled: e.target.checked })} />
            Automatically top up my wallet when it runs low
          </label>
          <div className="flex flex-wrap items-end gap-4" style={{ opacity: ar.enabled ? 1 : 0.5 }}>
            <label className="text-xs" style={{ color: "var(--text-muted)" }}>
              <div className="mb-1">When balance falls below (£)</div>
              <input className="input" type="number" min="0" step="1" style={{ width: 110 }} disabled={!ar.enabled}
                value={ar.thresholdPence / 100} onChange={(e) => setAr({ ...ar, thresholdPence: Math.round(Number(e.target.value) * 100) })} />
            </label>
            <label className="text-xs" style={{ color: "var(--text-muted)" }}>
              <div className="mb-1">Top up by (£)</div>
              <input className="input" type="number" min="1" step="1" style={{ width: 110 }} disabled={!ar.enabled}
                value={ar.amountPence / 100} onChange={(e) => setAr({ ...ar, amountPence: Math.round(Number(e.target.value) * 100) })} />
            </label>
            <button className="btn btn-primary" disabled={arBusy || (ar.enabled && ar.amountPence < 100)} onClick={saveAr}>Save</button>
          </div>
          <p className="text-xs mt-3" style={{ color: "var(--text-muted)" }}>
            Charges your saved card (from a subscription or a previous top-up). If the amount above wouldn't
            clear your balance and cover a month of your services, we top up the rounded amount that does.{" "}
            {ar.consecutiveFails > 0 && `Last ${ar.consecutiveFails} attempt(s) failed.`}
          </p>
        </Card>
      )}

      <Card className="!p-0 overflow-hidden">
        <table className="w-full text-sm" style={{ borderCollapse: "collapse" }}>
          <thead><tr style={{ color: "var(--text-muted)", textAlign: "left" }}>
            <th className="px-4 py-3 font-semibold">Date</th>
            <th className="px-4 py-3 font-semibold">Type</th>
            <th className="px-4 py-3 font-semibold">Amount</th>
            <th className="px-4 py-3 font-semibold">Notes</th>
          </tr></thead>
          <tbody>
            {data.recent_ledger.map((r) => (
              <tr key={r.id} style={{ borderTop: "1px solid var(--border)" }}>
                <td className="px-4 py-3" style={{ color: "var(--text-muted)" }}>{timeAgo(r.created_at)}</td>
                <td className="px-4 py-3" style={{ color: "var(--text)" }}>{r.type}</td>
                <td className="px-4 py-3" style={{ color: r.amount_pence < 0 ? "var(--err)" : "var(--ok, var(--text))" }}>
                  {r.amount_pence < 0 ? "−" : "+"}{gbp(Math.abs(r.amount_pence))}
                </td>
                <td className="px-4 py-3" style={{ color: "var(--text-muted)" }}>{r.notes || (r.period ? r.period : "—")}</td>
              </tr>
            ))}
            {data.recent_ledger.length === 0 && (
              <tr><td className="px-4 py-6" colSpan={4} style={{ color: "var(--text-muted)" }}>No transactions yet.</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
